import {
  getOfflineSessionOwner,
  isOwnedBy,
} from '@/lib/offline/session-offline-data';

export interface SignatureOutboxInput {
  jsaId: number;
  endpoint: string;
  method?: string;
  body: Record<string, unknown>;
  signerName?: string;
  capturedAt?: number;
  idempotencyKey?: string;
}

export interface QueuedSignature extends SignatureOutboxInput {
  id: number;
  ownerUserId: number | null;
  ownerOrgId: number | null;
  capturedAt: number;
  queuedAt: number;
  idempotencyKey: string;
  status: 'pending' | 'failed' | 'conflict';
  attempts: number;
  error?: string;
}

const DB_NAME = 'h3ight-signing-outbox';
const DB_VERSION = 1;
const STORE_NAME = 'signatures';

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // ignore listener failures
    }
  });
}

export function subscribeOutbox(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Failed to open signing outbox database'));
    request.onblocked = () => reject(new Error('Signing outbox database is blocked'));
  });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function transactionToPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'));
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'));
  });
}

function closeDatabase(db: IDBDatabase) {
  db.close();
}

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sig-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeRecord(
  input: SignatureOutboxInput,
  capturedAt: number,
  queuedAt: number,
): Omit<QueuedSignature, 'id' | 'ownerUserId' | 'ownerOrgId'> {
  return {
    ...input,
    method: input.method ?? 'POST',
    capturedAt,
    queuedAt,
    idempotencyKey: input.idempotencyKey ?? generateId(),
    status: 'pending',
    attempts: 0,
  };
}

export async function enqueueSignature(input: SignatureOutboxInput): Promise<QueuedSignature> {
  const db = await openDatabase();
  try {
    const capturedAt = input.capturedAt ?? Date.now();
    const queuedAt = Date.now();
    const owner = getOfflineSessionOwner();
    const record = {
      ...normalizeRecord(input, capturedAt, queuedAt),
      ownerUserId: owner?.userId ?? null,
      ownerOrgId: owner?.orgId ?? null,
    };
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const [id] = await Promise.all([
      requestToPromise<IDBValidKey>(store.add(record)),
      transactionToPromise(tx),
    ]);
    notify();
    return { ...record, id: Number(id) };
  } finally {
    closeDatabase(db);
  }
}

export async function listQueuedSignatures(): Promise<QueuedSignature[]> {
  const owner = getOfflineSessionOwner();
  const records = await listAllQueuedSignatures();
  return records.filter((record) => isOwnedBy(record, owner));
}

export async function listAllQueuedSignatures(): Promise<QueuedSignature[]> {
  const db = await openDatabase();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const [records] = await Promise.all([
      requestToPromise<QueuedSignature[]>(store.getAll()),
      transactionToPromise(tx),
    ]);
    return records.sort((left, right) => {
      if (left.capturedAt !== right.capturedAt) {
        return left.capturedAt - right.capturedAt;
      }
      return left.id - right.id;
    });
  } finally {
    closeDatabase(db);
  }
}

export async function countQueuedSignatures(): Promise<number> {
  const db = await openDatabase();
  try {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const [count] = await Promise.all([
      requestToPromise<number>(store.count()),
      transactionToPromise(tx),
    ]);
    return count;
  } finally {
    closeDatabase(db);
  }
}

export async function acknowledgeSignature(id: number): Promise<void> {
  const db = await openDatabase();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await Promise.all([
      requestToPromise(store.delete(id)),
      transactionToPromise(tx),
    ]);
    notify();
  } finally {
    closeDatabase(db);
  }
}

export async function updateQueuedSignature(id: number, patch: Partial<Omit<QueuedSignature, 'id'>>): Promise<void> {
  const db = await openDatabase();
  try {
    const readTx = db.transaction(STORE_NAME, 'readonly');
    const readStore = readTx.objectStore(STORE_NAME);
    const [existing] = await Promise.all([
      requestToPromise<QueuedSignature | undefined>(readStore.get(id)),
      transactionToPromise(readTx),
    ]);
    if (!existing) {
      return;
    }

    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const merged = { ...existing, ...patch };
    await Promise.all([
      requestToPromise(store.put(merged)),
      transactionToPromise(tx),
    ]);
    notify();
  } finally {
    closeDatabase(db);
  }
}

export async function clearOutbox(): Promise<void> {
  const db = await openDatabase();
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await Promise.all([
      requestToPromise(store.clear()),
      transactionToPromise(tx),
    ]);
    notify();
  } finally {
    closeDatabase(db);
  }
}
